import type { CoachEventCard } from '../lib/api'
import { formatDuration, formatTime, relativeTime } from '../lib/format'
import { AssignmentBadge, AvailabilityBadge, Card, EventStatusBadge, ServiceTag } from './ui'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function DateChip({ starts_at }: { starts_at: string }) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Dubai',
    day: 'numeric',
    month: 'numeric',
  }).formatToParts(new Date(starts_at))
  const day = parts.find((p) => p.type === 'day')?.value ?? ''
  const monthIndex = Number(parts.find((p) => p.type === 'month')?.value ?? '1') - 1
  return (
    <span className="card__when" aria-hidden="true">
      <span className="card__when-day">{day}</span>
      <span className="card__when-mon">{MONTHS[monthIndex]}</span>
    </span>
  )
}

/**
 * One event as a coach sees it. The relationship line at the bottom is the
 * point: a coach scanning this list needs to know instantly whether the event
 * is waiting on them.
 */
export function CoachEventListItem({
  card,
  to,
}: {
  card: CoachEventCard
  to: string
}) {
  const { event, assignment, availability } = card
  const needsAnswer = !assignment && availability?.response === 'pending'
  const needsConfirm = assignment?.status === 'assigned'
  const tone = needsAnswer || needsConfirm ? 'attention' : 'default'

  return (
    <Card to={to} tone={tone}>
      <div className="row">
        <DateChip starts_at={event.starts_at} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <p className="card__title">{event.title}</p>
          <div className="card__meta">
            <span>{formatTime(event.starts_at)}</span>
            <span aria-hidden="true">&middot;</span>
            <span>{formatDuration(event.duration_minutes)}</span>
            <span aria-hidden="true">&middot;</span>
            <span>{event.location_name}</span>
          </div>
          <div className="card__meta">
            <ServiceTag service={event.service} />
          </div>
        </div>
      </div>

      <div className="card__foot">
        {event.status === 'cancelled' ? (
          <EventStatusBadge status="cancelled" />
        ) : assignment ? (
          <AssignmentBadge status={assignment.status} />
        ) : availability ? (
          <AvailabilityBadge response={availability.response} />
        ) : null}
        <span className="spacer" />
        <span className="faint">{relativeTime(event.starts_at)}</span>
      </div>
    </Card>
  )
}
